/**
 * User management service for admin interface
 * Feature: 001-user-role-management (Phase 4 - T063)
 * Security: Uses JWT-based authentication (no insecure query parameters)
 */
import { createApiClient } from '../lib/api';
import type {
  User,
  UserListResponse,
  UserInvitation,
  InviteUserRequest,
  UpdateUserStatusRequest,
  UpdateUserRoleRequest,
} from '../types/user';

/**
 * List all users with pagination and filtering
 * Requires admin role (validated via JWT token)
 */
export async function listUsers(
  getToken: () => Promise<string | null>,
  params: {
    page?: number;
    size?: number;
    role?: string;
    sync_status?: string;
  }
): Promise<UserListResponse> {
  const api = createApiClient(getToken);
  const response = await api.get<UserListResponse>('/users', {
    params,
  });
  return response.data;
}

/**
 * Get a single user by ID
 * Requires admin role (validated via JWT token)
 */
export async function getUser(
  getToken: () => Promise<string | null>,
  userId: string
): Promise<User> {
  const api = createApiClient(getToken);
  const response = await api.get<User>(`/users/${userId}`);
  return response.data;
}

/**
 * Invite a new user via Clerk
 * Requires admin role (validated via JWT token)
 */
export async function inviteUser(
  getToken: () => Promise<string | null>,
  data: InviteUserRequest
): Promise<UserInvitation> {
  const api = createApiClient(getToken);
  const response = await api.post<UserInvitation>('/users/invite', data);
  return response.data;
}

/**
 * Sync a user from Clerk to local database
 * Requires admin role (validated via JWT token)
 */
export async function syncUser(
  getToken: () => Promise<string | null>,
  userId: string
): Promise<User> {
  const api = createApiClient(getToken);
  const response = await api.post<User>(`/users/${userId}/sync`, null);
  return response.data;
}

/**
 * Update user status (block/unblock/delete)
 * Requires admin role (validated via JWT token)
 */
export async function updateUserStatus(
  getToken: () => Promise<string | null>,
  userId: string,
  data: UpdateUserStatusRequest
): Promise<User> {
  const api = createApiClient(getToken);
  const response = await api.put<User>(`/users/${userId}/status`, data);
  return response.data;
}

/**
 * Update user role
 * Requires admin role (validated via JWT token)
 */
export async function updateUserRole(
  getToken: () => Promise<string | null>,
  userId: string,
  data: UpdateUserRoleRequest
): Promise<User> {
  const api = createApiClient(getToken);
  const response = await api.put<User>(`/users/${userId}/role`, data);
  return response.data;
}

/**
 * Get list of pending invitations
 * Requires admin role (validated via JWT token)
 */
export async function getInvitations(
  getToken: () => Promise<string | null>
): Promise<UserInvitation[]> {
  const api = createApiClient(getToken);
  const response = await api.get<UserInvitation[]>('/users/invitations');
  return response.data;
}

/**
 * Delete a user from local database and Clerk
 * Requires admin role (validated via JWT token)
 */
export async function deleteUser(
  getToken: () => Promise<string | null>,
  userId: string
): Promise<{ message: string }> {
  const api = createApiClient(getToken);
  const response = await api.delete<{ message: string }>(`/users/${userId}`);
  return response.data;
}

export const userService = {
  listUsers,
  getUser,
  inviteUser,
  syncUser,
  updateUserStatus,
  updateUserRole,
  getInvitations,
  deleteUser,
};

